import { Placement } from '@floating-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';

interface OverflowTextProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string;
  className?: string;
  truncateType?: 'center' | 'end';
  placement?: Placement;
}

export const OverflowText = React.memo((props: OverflowTextProps) => {
  const {
    text = '',
    className = '',
    truncateType = 'end',
    placement = 'bottom',
    onClick,
    ...rest
  } = props;

  const [isOverflowed, setIsOverflow] = useState(false);
  const textElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = textElementRef.current;
    if (!element) return;

    const checkOverflow = () => {
      const isOverflow = element.scrollWidth > element.clientWidth;
      setIsOverflow(prev => (prev !== isOverflow ? isOverflow : prev));
    };

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    checkOverflow(); // Initial check

    return () => resizeObserver.disconnect();
  }, []);

  if (truncateType === 'end') {
    return (
      <Tooltip
        tooltipClassName="break-words"
        title={isOverflowed ? text : ''}
        placement={placement}
      >
        <div
          ref={textElementRef}
          className={`truncate ${className}`}
          onClick={onClick}
          {...rest}
        >
          {text}
        </div>
      </Tooltip>
    );
  }

  // For center truncation
  const start = text.slice(0, text.length / 2);
  const end = text.slice(text.length / 2);

  return (
    <Tooltip
      tooltipClassName="break-words"
      title={isOverflowed ? text : ''}
      placement={placement}
    >
      <div
        ref={textElementRef}
        className={`overflow-hidden ${className}`}
        onClick={onClick}
      >
        <div className="flex">
          <div className="truncate">{start}</div>
          <div className="overflow-hidden whitespace-nowrap">{end}</div>
        </div>
      </div>
    </Tooltip>
  );
});

OverflowText.displayName = 'OverflowText';
